import { useState } from "react";
import { toast } from "sonner";
import { Gift, SlidersHorizontal, Ticket, ToggleLeft, X } from "lucide-react";
import { http } from "@/lib/api";
import { FEATURE_NAMES, PLAN_NAMES, day } from "@/lib/staff";
import { Field, INPUT, Select } from "@/components/staff/ui";
import { ConfirmAction } from "@/components/staff/ConfirmAction";

const GIVE = [["starter", "Starter"], ["plus", "Plus"], ["unlimited", "Unlimited"]];
const post = (uid, path, body) => http.post(`/staff/students/${uid}/${path}`, body);

function GivePlan({ uid, onDone }) {
  const [tier, setTier] = useState("plus");
  const [days, setDays] = useState("30");
  const n = Number(days);
  return (
    <ConfirmAction label="Give a free plan" icon={Gift} testid="action-give-plan" title="Give a free plan" confirmLabel="Give plan"
      description="The student gets this plan at no charge until it ends. Nothing is charged in Stripe." canSubmit={n >= 1 && n <= 366}
      run={async (reason) => { await post(uid, "plan", { tier, days: n, reason }); toast.success("Free plan given"); onDone(); }}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Plan"><Select value={tier} onChange={setTier} options={GIVE} label="Plan" testid="action-give-plan-tier" /></Field>
        <Field label="Days (1–366)">
          <input type="number" min={1} max={366} value={days} onChange={(e) => setDays(e.target.value)} className={`${INPUT} w-full`} data-testid="action-give-plan-days" />
        </Field>
      </div>
    </ConfirmAction>
  );
}

const RemovePlan = ({ uid, comp, onDone }) => (
  <ConfirmAction label="Remove free plan" icon={X} danger testid="action-remove-plan" title="Remove the free plan?" confirmLabel="Remove plan"
    description={`${PLAN_NAMES[comp.tier]} (until ${day(comp.ends_at)}) ends now. Anything they pay for in Stripe isn't affected.`}
    run={async (reason) => { await post(uid, "plan", { tier: null, reason }); toast.success("Free plan removed"); onDone(); }} />
);

function GivePass({ uid, onDone }) {
  const [tier, setTier] = useState("plus");
  return (
    <ConfirmAction label="Give a free pass" icon={Ticket} testid="action-give-pass" title="Give a free pass" confirmLabel="Give pass"
      description="The pass waits until the student starts using it. Nothing is charged in Stripe."
      run={async (reason) => { await post(uid, "passes", { tier, reason }); toast.success("Free pass given"); onDone(); }}>
      <Field label="Pass"><Select value={tier} onChange={setTier} options={GIVE} label="Pass" testid="action-give-pass-tier" /></Field>
    </ConfirmAction>
  );
}

export const RemovePass = ({ uid, pass, onDone }) => (
  <ConfirmAction label="Remove" danger testid={`action-remove-pass-${pass.pass_id}`} title={`Remove this ${pass.name}?`} confirmLabel="Remove pass"
    description="Only passes given by staff can be removed here." run={async (reason) => { await post(uid, `passes/${pass.pass_id}/remove`, { reason }); toast.success("Pass removed"); onDone(); }} />
);

const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function LimitsAction({ d, onDone }) {
  const cur = d.profile.limit_overrides || {};
  const [voice, setVoice] = useState(cur.voice ?? "");
  const [draw, setDraw] = useState(cur.draw ?? "");
  const ok = [voice, draw].every((v) => v === "" || (Number(v) >= 0 && Number(v) <= 1000));
  return (
    <ConfirmAction label="Personal daily limits" icon={SlidersHorizontal} testid="action-limits" title="Personal daily limits" confirmLabel="Save limits" canSubmit={ok}
      description="Leave a box empty to use the plan's limit. Limits reset at midnight UTC."
      run={async (reason) => { await post(d.profile.user_id, "limits", { voice: toNum(voice), draw: toNum(draw), reason }); toast.success("Limits saved"); onDone(); }}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Listen & Mic per day"><input type="number" min={0} max={1000} value={voice} onChange={(e) => setVoice(e.target.value)} placeholder="Plan limit" className={`${INPUT} w-full`} data-testid="action-limits-voice" /></Field>
        <Field label="Draw it per day"><input type="number" min={0} max={1000} value={draw} onChange={(e) => setDraw(e.target.value)} placeholder="Plan limit" className={`${INPUT} w-full`} data-testid="action-limits-draw" /></Field>
      </div>
    </ConfirmAction>
  );
}

const SWITCH = [["", "Plan default"], ["on", "On"], ["off", "Off"]];
const fromBool = (v) => (v === true ? "on" : v === false ? "off" : "");
const toBool = (v) => (v === "on" ? true : v === "off" ? false : null);

function FeaturesAction({ d, onDone }) {
  const cur = d.profile.feature_overrides || {};
  const [vals, setVals] = useState(() => Object.fromEntries(Object.keys(FEATURE_NAMES).map((f) => [f, fromBool(cur[f])])));
  return (
    <ConfirmAction label="Feature switches" icon={ToggleLeft} testid="action-features" title="Feature switches for this student" confirmLabel="Save switches"
      description="On or Off overrides the student's plan for that feature. Plan default follows their plan."
      run={async (reason) => {
        await post(d.profile.user_id, "features", { features: Object.fromEntries(Object.entries(vals).map(([f, v]) => [f, toBool(v)])), reason });
        toast.success("Feature switches saved"); onDone();
      }}>
      <div className="space-y-2">
        {Object.entries(FEATURE_NAMES).map(([f, label]) => (
          <div key={f} className="flex items-center justify-between gap-3 text-sm">
            <span>{label}</span>
            <Select value={vals[f]} onChange={(v) => setVals((x) => ({ ...x, [f]: v }))} options={SWITCH} label={label} testid={`action-feature-${f}`} />
          </div>
        ))}
      </div>
    </ConfirmAction>
  );
}

export { GivePlan, RemovePlan, GivePass, LimitsAction, FeaturesAction };
